import i18n from "i18next";
import { batchBCopy } from "./i18n/batch-b";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import EmailTemplates from "./pages/super/content/EmailTemplates";
import { moduleCopy } from "./i18n-modules";
import { registerPseudoLocale } from "./lib/i18n/pseudo-locale";
import { authUiEn, authUiMs } from "./lib/i18n/auth-ui";
import { shellUiEn, shellUiMs } from "./lib/i18n/shell-ui";
import { kitUiEn, kitUiMs } from "./lib/i18n/kit-ui";
import { dashUiEn, dashUiMs } from "./lib/i18n/dash-ui";
import { waveUiEn, waveUiMs } from "./lib/i18n/wave-ui";
import enBatch from "./locales/en/batch-c.json";
import msBatch from "./locales/ms/batch-c.json";
import enMarketing from "./locales/en/marketing.json";
import msMarketing from "./locales/ms/marketing.json";
import enToasts from "./locales/en/toasts.json";
import msToasts from "./locales/ms/toasts.json";
import { htmlLang } from "./lib/batch-c";
import { batchACopy } from "./i18n-batch-a";

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: "Home",
        features: "Features",
        about: "About",
        contact: "Contact",
        dashboard: "Dashboard",
        profile: "Profile",
        settings: "Settings",
        logout: "Logout",
      },

      // Auth page
      auth: {
        title: "Welcome to FanJago",
        subtitle: "Sign in to your account or create a new one",
        signIn: "Sign In",
        signUp: "Sign Up",
        email: "Email Address",
        password: "Password",
        forgotPassword: "Forgot your password?",
        enterArena: "Enter the Arena",
        signingIn: "Signing in...",
        continueWithGoogle: "Continue with Google",
        continueWithFacebook: "Continue with Facebook",
        orContinueWith: "or continue with",
        joinAthletes: "Join {{count}}+ sports enthusiasts across Malaysia",
        welcomeBack: "Welcome Back, Athlete! Ready to get back in the game?",
        resetPassword: "Reset your password",
        sendResetLink: "Send reset link",
        passwordResetSent: "Password reset email sent",
        generatedEmailReset:
          "This is a generated child email. A linked parent can set a new password from Children.",
        generatedParentReset:
          "If this parent email can receive mail, a reset link is on its way. A generated @fanjago.com address cannot receive mail until it is changed.",
        emailNotFound:
          "This email doesn't exist in our system. Please check and try again.",
        invalidEmail: "Invalid email address",
        passwordRequired: "Password is required",
        passwordMinLength: "Password must be at least {{minLength}} characters",
        passwordsDoNotMatch: "Passwords do not match",
        updatePassword: "Update Password",
        newPassword: "New Password",
        confirmPassword: "Confirm Password",
        enterNewPassword: "Enter your new password below",
        passwordUpdatedSuccess: "Password updated successfully!",
        backToLogin: "Back to Login",
        resetYourPassword: "Reset Your Password",
        secureYourAccount: "Secure Your Account",
        createStrongPassword:
          "Create a strong password to protect your account",
        strongPasswordRequired: "Strong Password Required",
        passwordRequirements: "At least 6 characters long",
        nameRequired: "Name must be at least 2 characters",
        nameMaxLength: "Name must be at most 100 characters",
        pleaseCompleteCaptcha: "Please complete the hCaptcha challenge",
        captchaVerificationFailed: "hCaptcha verification failed",
        redirectingToPasswordReset: "Redirecting to password reset...",
        redirectingToPasswordUpdate: "Redirecting to password update...",
      },

      // Landing page
      landing: {
        hero: {
          title: "THE FUTURE OF FOOTBALL",
          subtitle: "STARTS HERE",
          description:
            "Manage clubs, discover talent, develop players, and organize competitions — all in one smart football ecosystem.",
          getStartedFree: "Get Started Free",
          alreadyHaveAccount: "Already have an account?",
          signIn: "Sign In",
        },
        features: {
          title: "Everything You Need To Elevate Your Football Journey",
          description:
            "From live match tracking to AI-powered insights, FanJago provides all the tools to manage, develop, and grow your football ecosystem.",
          liveMatchCoverage: "Live Match Coverage",
          liveMatchDescription:
            "Track match events in real time — goals, cards, substitutions and more.",
          advancedPlayerStats: "Advanced Player Stats",
          advancedPlayerDescription:
            "Analyze performance with detailed insights powered by live data.",
          aiScoutingAssistant: "AI Scouting Assistant",
          aiScoutingDescription:
            "Discover hidden gems and recommend rising talent automatically.",
          clubTeamManagement: "Club & Team Management",
          clubTeamDescription:
            "Manage squads, schedules, and staff all in one place.",
          playerDevelopmentTracking: "Player Development Tracking",
          playerDevelopmentDescription:
            "Monitor growth over time with progress charts and benchmarking.",
          tournamentOrganizerTools: "Tournament Organizer Tools",
          tournamentOrganizerDescription:
            "Create tournaments, fixtures, standings, and automate results.",
        },
        stats: {
          activeAthletes: "Active Athletes",
          clubsAcademies: "Clubs & Academies",
          matchesTracked: "Matches Tracked",
          tournamentsOrganized: "Tournaments Organized",
        },
        footer: {
          company: "Company",
          platform: "Platform",
          legal: "Legal",
          social: "Social",
          allRightsReserved: "© {{year}} FanJago. All rights reserved.",
          malaysia: "Malaysia",
          englishMY: "English (MY)",
        },
        marketing: {
          buildYourTeam: "Build Your Team",
          buildYourTeamDescription:
            "Connect with athletes, coaches, and sports communities",
          achieveExcellence: "Achieve Excellence",
          achieveExcellenceDescription:
            "Track training progress and reach sporting milestones",
          winTogether: "Win Together",
          winTogetherDescription:
            "Access professional training and championship opportunities",
          activeAthletes: "8,500+ Active Athletes",
          fromGrassroots:
            "From grassroots to glory – FanJago is where Malaysian sporting dreams come alive.",
        },
      },

      // Common
      common: {
        loading: "Loading...",
        error: "Error",
        success: "Success",
        cancel: "Cancel",
        save: "Save",
        delete: "Delete",
        edit: "Edit",
        create: "Create",
        update: "Update",
        search: "Search",
        filter: "Filter",
        sort: "Sort",
        next: "Next",
        previous: "Previous",
        confirm: "Confirm",
        close: "Close",
        active: "Active",
        inactive: "Inactive",
      },

      // Breadcrumbs
      breadcrumbs: {
        details: "Details",
      },

      playerProfile: {
        basicInfo: "Basic Info",
        position: "Position",
        country: "Country",
        height: "Height",
        weight: "Weight",
        jersey: "Jersey",
        preferredFoot: "Preferred Foot",
        dob: "Date of Birth",
      },

      // Subscription & Billing
      subscription: {
        // General
        subscription: "Subscription",
        upgrade: "Upgrade",
        downgrade: "Downgrade",
        renew: "Renew",
        cancel: "Cancel",
        expired: "Expired",
        active: "Active",
        cancelled: "Cancelled",
        pending: "Pending",

        // Plans
        choosePlan: "Choose Your Plan",
        comparePlans: "Compare Plans",
        currentPlan: "Current Plan",
        upgradeTo: "Upgrade to {{plan}}",
        mostPopular: "Most Popular",
        bestValue: "Best Value",

        // Features
        premiumFeatures: "Premium Features",
        premiumAI: "Premium AI Insights",
        visibilityBoost: "Visibility Boost",
        tournamentDiscount: "Tournament Fee Discount",
        playerLimit: "Player Limit",
        unlimited: "Unlimited",

        // Billing
        billingCycle: "Billing Cycle",
        monthly: "Monthly",
        yearly: "Yearly",
        savePercent: "Save {{percent}}%",

        // Email Templates
        emailTemplates: {
          title: "Email Templates",
          description: "Manage email templates for subscription notifications",
          addTemplate: "Add Template",
          editTemplate: "Edit Template",
          templateDescription:
            "Create and edit email templates for customer notifications",
          saveSuccess: "Template saved successfully",
          saveError: "Failed to save template",
          deleteSuccess: "Template deleted successfully",
          deleteError: "Failed to delete template",
          noTemplates: "No email templates available",
          createFirst: "Create your first template",
        },
        perMonth: "per month",
        perYear: "per year",

        // Payment
        securePayment: "Secure Payment",
        processing: "Processing...",
        paymentSuccess: "Payment Successful!",
        paymentFailed: "Payment Failed",
        tryAgain: "Try Again",

        // Status messages
        subscriptionExpired: "Your subscription has expired",
        subscriptionExpiring: "Your subscription expires in {{days}} days",
        subscriptionCancelled: "Your subscription has been cancelled",
        subscriptionActive: "Your subscription is active",

        // Upgrade prompts
        upgradeToAccess: "Upgrade to access this feature",
        unlockPremium: "Unlock Premium Features",
        getStarted: "Get Started",

        // Email content templates
        email: {
          subject: {
            welcome: "Welcome to FanJago Premium!",
            paymentConfirmation: "Payment Confirmation - FanJago",
            subscriptionRenewal: "Subscription Renewal Reminder",
            subscriptionExpired: "Your Subscription Has Expired",
            paymentFailed: "Payment Failed - Action Required",
          },
          greeting: "Hi {{name}},",
          thanks: "Thank you for choosing FanJago!",
          regards: "Best regards,\nThe FanJago Team",
          questions: "Questions? Contact us at support@fanjago.com",
        },
      },

      welfare: {
        title: "Player Welfare",
        subtitle: {
          staff: "Report injuries and walk players through return-to-play.",
          medic: "Review return-to-play and clear players when they are ready.",
          player: "Your availability and return-to-play progress.",
          parent: "Follow your child's injury and acknowledge clearance.",
        },
        reportInjury: "Report injury",
        reportHelp: "The player stays unavailable until a coach or medic clears them. Under-18 players on your teams are listed even when they are not assigned to a club.",
        reportSaved: "Injury reported. Linked parents have been notified.",
        reportSavedNoParent: "Injury reported. This player has no linked parent to notify.",
        reportSavedUnconfirmed: "Injury reported.",
        stageSaved: "Stage updated.",
        clearanceSaved: "Clearance recorded.",
        acknowledged: "Clearance acknowledged.",
        team: "Team",
        teamHelp: "Injuries are recorded against the player's team.",
        child: "Child",
        player: "Player",
        playerFallback: "Player",
        u18: "Under 18",
        noClub: "No club",
        injuryType: "Injury type",
        injuryDate: "Date",
        notes: "Notes",
        save: "Save",
        loading: "Loading...",
        noTeams: "No teams are linked to your coach or manager role.",
        noChildren: "No linked children yet.",
        noInjuries: "No welfare injuries recorded.",
        loadError: "Welfare records could not be loaded.",
        markComplete: "Mark stage complete",
        stageComplete: "Complete",
        stagePending: "Not started",
        clearPlayer: "Clear player",
        acknowledge: "Acknowledge clearance",
        parentAckTitle: "Parent acknowledgement required",
        parentAckHelp: "An under-18 player stays unavailable until a linked parent acknowledges this clearance.",
        playerUnavailableHelp: "You are unavailable until a coach or medic clears you. Under-18 players also need a parent to acknowledge that clearance.",
        playerAvailableHelp: "You are available for selection.",
        concussionTitle: "Suspected concussion",
        concussionHelp: "Remove the player immediately. No same-day return. Six stages, at least 24 hours each. Drop back a stage if symptoms return. A doctor's letter is required before contact and match play.",
        availability: {
          available: "Available",
          unavailable: "Unavailable",
          column: "Availability",
        },
        status: {
          reported: "Reported",
          in_rtp: "Return to play",
          pending_parent_ack: "Awaiting parent",
          cleared: "Cleared",
        },
        types: {
          suspected_concussion: "Suspected concussion",
          muscle: "Muscle",
          joint: "Joint",
          ligament: "Ligament",
          fracture: "Fracture",
          illness: "Illness",
          other: "Other",
        },
        steps: {
          concussion_relative_rest: "Relative rest",
          concussion_light_aerobic: "Light aerobic exercise",
          concussion_sport_specific: "Sport-specific exercise",
          concussion_non_contact: "Non-contact training",
          concussion_full_contact: "Full-contact practice",
          concussion_return: "Return to play",
          general_rest: "Rest and protection",
          general_light: "Light activity",
          general_modified: "Modified training",
          general_full: "Full training",
          general_return: "Return to play",
        },
        stepHelp: {
          concussion_relative_rest: "Relative rest for at least 24 hours. No same-day return.",
          concussion_light_aerobic: "Light exercise only, with no heading.",
          concussion_sport_specific: "Football-specific movement, with no heading or contact.",
          concussion_non_contact: "Training drills without contact.",
          concussion_full_contact: "Full contact only after a doctor's letter and 24 hours.",
          concussion_return: "Match play only after the doctor's letter and 24 hours.",
          general_rest: "Protect the injury for at least 24 hours.",
          general_light: "Light activity once rest is complete.",
          general_modified: "Training modified around the injury.",
          general_full: "Full training with the squad.",
          general_return: "Available for selection after this stage and clearance.",
        },
        stepBlock: {
          previous: "Complete the previous stage first.",
          rest: "The minimum rest for this stage has not elapsed.",
          gap: "Wait 24 hours after the previous stage.",
          sameDay: "No same-day return after a suspected concussion.",
          clearance: "Upload the doctor's letter before contact training or match play.",
        },
        heading: {
          label: "Heading practice",
          included: "Includes heading",
          notIncluded: "No heading",
          none: "No heading. This age group is U11 or younger.",
          limited_5: "U12-U16: at most one heading session a week, about 5 headers.",
          standard_10: "Older players: at most one heading session a week, about 10 headers.",
          age_unknown: "Set the programme age group before heading is allowed.",
          blocked: "Heading is blocked for this age group.",
        },
      },
    },
  },
  ms: {
    translation: {
      // Navigation
      nav: {
        home: "Laman Utama",
        features: "Ciri-ciri",
        about: "Tentang",
        contact: "Hubungi",
        dashboard: "Papan Pemuka",
        profile: "Profil",
        settings: "Tetapan",
        logout: "Log Keluar",
      },

      // Auth page
      auth: {
        title: "Selamat Datang ke FanJago",
        subtitle: "Log masuk ke akaun anda atau buka yang baharu",
        signIn: "Log Masuk",
        signUp: "Daftar",
        email: "Alamat e-mel",
        password: "Kata Laluan",
        forgotPassword: "Lupa kata laluan anda?",
        enterArena: "Masuk ke Gelanggang",
        signingIn: "Sedang log masuk...",
        continueWithGoogle: "Teruskan dengan Google",
        continueWithFacebook: "Teruskan dengan Facebook",
        orContinueWith: "atau teruskan dengan",
        joinAthletes: "Sertai {{count}}+ peminat sukan di seluruh Malaysia",
        welcomeBack:
          "Selamat Kembali, Atlet! Sedia untuk kembali ke permainan?",
        resetPassword: "Tetapkan semula kata laluan anda",
        sendResetLink: "Hantar pautan tetapan semula",
        passwordResetSent: "E-mel tetapan semula kata laluan telah dihantar",
        generatedEmailReset:
          "Ini e-mel anak yang dijana. Ibu bapa yang dipautkan boleh tetapkan kata laluan baharu dari halaman Anak.",
        generatedParentReset:
          "Jika e-mel ibu bapa ini boleh menerima mel, pautan tetapan semula sedang dihantar. Alamat @fanjago.com yang dijana tidak menerima mel sehingga ia ditukar.",
        emailNotFound:
          "E-mel ini tidak wujud dalam sistem kami. Sila semak dan cuba lagi.",
        invalidEmail: "Alamat e-mel tidak sah",
        passwordRequired: "Kata laluan diperlukan",
        passwordMinLength:
          "Kata laluan mesti sekurang-kurangnya {{minLength}} aksara",
        passwordsDoNotMatch: "Kata laluan tidak sepadan",
        updatePassword: "Kemas Kini Kata Laluan",
        newPassword: "Kata Laluan Baharu",
        confirmPassword: "Sahkan Kata Laluan",
        enterNewPassword: "Masukkan kata laluan baharu anda di bawah",
        passwordUpdatedSuccess: "Kata laluan berjaya dikemas kini!",
        backToLogin: "Kembali ke Log Masuk",
        resetYourPassword: "Tetapkan Semula Kata Laluan Anda",
        secureYourAccount: "Selamatkan Akaun Anda",
        createStrongPassword:
          "Cipta kata laluan yang kukuh untuk melindungi akaun anda",
        strongPasswordRequired: "Kata Laluan Kukuh Diperlukan",
        passwordRequirements: "Sekurang-kurangnya 6 aksara panjang",
        nameRequired: "Nama mesti sekurang-kurangnya 2 aksara",
        nameMaxLength: "Nama mesti selebih-lebihnya 100 aksara",
        pleaseCompleteCaptcha: "Sila lengkapkan cabaran hCaptcha",
        captchaVerificationFailed: "Pengesahan hCaptcha gagal",
        redirectingToPasswordReset: "Menghala ke tetapan semula kata laluan...",
        redirectingToPasswordUpdate: "Menghala ke kemas kini kata laluan...",
      },

      // Landing page
      landing: {
        hero: {
          title: "MASA DEPAN BOLA SEPAK",
          subtitle: "BERMULA DI SINI",
          description:
            "Urus kelab, temui bakat, bangunkan pemain, dan anjurkan pertandingan — semua dalam satu ekosistem bola sepak pintar.",
          getStartedFree: "Mulakan Percuma",
          alreadyHaveAccount: "Sudah mempunyai akaun?",
          signIn: "Log Masuk",
        },
        features: {
          title:
            "Segala Yang Anda Perlukan Untuk Meningkatkan Perjalanan Bola Sepak Anda",
          description:
            "Daripada penjejakan perlawanan langsung kepada cerapan berkuasa AI, FanJago menyediakan semua alat untuk mengurus, membangunkan, dan mengembangkan ekosistem bola sepak anda.",
          liveMatchCoverage: " Liputan Perlawanan Langsung",
          liveMatchDescription:
            "Jejak acara perlawanan secara masa nyata — gol, kad, penggantian dan banyak lagi.",
          advancedPlayerStats: "Statistik Pemain Lanjutan",
          advancedPlayerDescription:
            "Analisis prestasi dengan cerapan terperinci yang dikuasakan oleh data langsung.",
          aiScoutingAssistant: "Pembantu pengakap AI",
          aiScoutingDescription:
            "Temui permata tersembunyi dan syorkan bakat yang sedang meningkat secara automatik.",
          clubTeamManagement: "Pengurusan Kelab & Pasukan",
          clubTeamDescription:
            "Urus skuad, jadual, dan kakitangan semua di satu tempat.",
          playerDevelopmentTracking: "Penjejakan Pembangunan Pemain",
          playerDevelopmentDescription:
            "Pantau pertumbuhan dari masa ke masa dengan carta kemajuan dan penanda aras.",
          tournamentOrganizerTools: "Alat Penganjur Kejohanan",
          tournamentOrganizerDescription:
            "Cipta kejohanan, perlawanan, kedudukan, dan automatikkan keputusan.",
        },
        stats: {
          activeAthletes: "Atlet Aktif",
          clubsAcademies: "Kelab & Akademi",
          matchesTracked: "Perlawanan Dijejak",
          tournamentsOrganized: "Kejohanan Dianjurkan",
        },
        footer: {
          company: "Syarikat",
          platform: "Platform",
          legal: "Undang-undang",
          social: "Sosial",
          allRightsReserved: "© {{year}} FanJago. Hak cipta terpelihara.",
          malaysia: "Malaysia",
          englishMY: "Bahasa Inggeris (MY)",
        },
        marketing: {
          buildYourTeam: "Bina Pasukan Anda",
          buildYourTeamDescription:
            "Berhubung dengan atlet, jurulatih, dan komuniti sukan",
          achieveExcellence: "Capai Kecemerlangan",
          achieveExcellenceDescription:
            "Jejak kemajuan latihan dan capai tonggak sukan",
          winTogether: "Menang Bersama",
          winTogetherDescription:
            "Akses latihan profesional dan peluang kejuaraan",
          activeAthletes: "8,500+ Atlet Aktif",
          fromGrassroots:
            "Dari akar umbi ke gemilang – FanJago adalah tempat impian sukan Malaysia menjadi nyata.",
        },
      },

      // Common
      common: {
        loading: "Memuatkan...",
        error: "Ralat",
        success: "Berjaya",
        cancel: "Batal",
        save: "Simpan",
        delete: "Padam",
        edit: "Sunting",
        create: "Cipta",
        update: "Kemas kini",
        search: "Cari",
        filter: "Tapis",
        sort: "Isih",
        next: "Seterusnya",
        previous: "Sebelumnya",
        confirm: "Sahkan",
        close: "Tutup",
        active: "Aktif",
        inactive: "Tidak Aktif",
      },

      // Breadcrumbs
      breadcrumbs: {
        details: "Butiran",
      },

      playerProfile: {
        basicInfo: "Maklumat Asas",
        position: "Posisi",
        country: "Negara",
        height: "Ketinggian",
        weight: "Berat",
        jersey: "Jersi",
        preferredFoot: "Kaki Dominan",
        dob: "Tarikh Lahir",
      },

      // Subscription & Billing
      subscription: {
        // General
        subscription: "Langganan",
        upgrade: "Naik Taraf",
        downgrade: "Turun Taraf",
        renew: "Baharui",
        cancel: "Batalkan",
        expired: "Tamat Tempoh",
        active: "Aktif",
        cancelled: "Dibatalkan",
        pending: "Menunggu",

        // Plans
        choosePlan: "Pilih Pelan Anda",
        comparePlans: "Bandingkan Pelan",
        currentPlan: "Pelan Semasa",
        upgradeTo: "Naik Taraf ke {{plan}}",
        mostPopular: "Paling Popular",
        bestValue: "Nilai Terbaik",

        // Features
        premiumFeatures: "Ciri Premium",
        premiumAI: "Panduan AI Premium",
        visibilityBoost: "Rangsangan keterlihatan",
        tournamentDiscount: "Diskaun yuran kejohanan",
        playerLimit: "Had Pemain",
        unlimited: "Tidak Terhad",

        // Billing
        billingCycle: "Kitaran Bil",
        monthly: "Bulanan",
        yearly: "Tahunan",
        savePercent: "Jimat {{percent}}%",

        // Email Templates
        emailTemplates: {
          title: "Templat e-mel",
          description: "Urus templat e-mel untuk pemberitahuan langganan",
          addTemplate: "Tambah Templat",
          editTemplate: "Sunting Templat",
          templateDescription:
            "Cipta dan sunting templat e-mel untuk pemberitahuan pelanggan",
          saveSuccess: "Templat berjaya disimpan",
          saveError: "Gagal menyimpan templat",
          deleteSuccess: "Templat berjaya dipadam",
          deleteError: "Gagal memadam templat",
          noTemplates: "Tiada templat e-mel tersedia",
          createFirst: "Cipta templat pertama anda",
        },
        perMonth: "sebulan",
        perYear: "setahun",

        // Payment
        securePayment: "Pembayaran Selamat",
        processing: "Memproses...",
        paymentSuccess: "Pembayaran Berjaya!",
        paymentFailed: "Pembayaran Gagal",
        tryAgain: "Cuba Lagi",

        // Status messages
        subscriptionExpired: "Langganan anda telah tamat tempoh",
        subscriptionExpiring: "Langganan anda akan tamat dalam {{days}} hari",
        subscriptionCancelled: "Langganan anda telah dibatalkan",
        subscriptionActive: "Langganan anda aktif",

        // Upgrade prompts
        upgradeToAccess: "Naik taraf untuk akses ciri ini",
        unlockPremium: "Buka Kunci Ciri Premium",
        getStarted: "Mulakan",

        // Email content templates
        email: {
          subject: {
            welcome: "Selamat Datang ke FanJago Premium!",
            paymentConfirmation: "Pengesahan Pembayaran - FanJago",
            subscriptionRenewal: "Peringatan Pembaharuan Langganan",
            subscriptionExpired: "Langganan Anda Telah Tamat Tempoh",
            paymentFailed: "Pembayaran Gagal - Tindakan Diperlukan",
          },
          greeting: "Hai {{name}},",
          thanks: "Terima kasih kerana memilih FanJago!",
          regards: "Salam hormat,\nPasukan FanJago",
          questions: "Soalan? Hubungi kami di support@fanjago.com",
        },
      },

      welfare: {
        title: "Kebajikan Pemain",
        subtitle: {
          staff: "Laporkan kecederaan dan ikuti protokol kembali bermain.",
          medic: "Semak protokol kembali bermain dan benarkan pemain yang sudah bersedia.",
          player: "Status kesediaan anda dan kemajuan kembali bermain.",
          parent: "Ikuti kecederaan anak anda dan sahkan pelepasan.",
        },
        reportInjury: "Laporkan kecederaan",
        reportHelp: "Pemain kekal tidak boleh bermain sehingga jurulatih atau pegawai perubatan membenarkannya. Pemain bawah 18 tahun dalam pasukan anda disenaraikan walaupun mereka tidak ditugaskan ke kelab.",
        reportSaved: "Kecederaan dilaporkan. Ibu bapa yang dipautkan telah dimaklumkan.",
        reportSavedNoParent: "Kecederaan dilaporkan. Pemain ini tiada ibu bapa yang dipautkan untuk dimaklumkan.",
        reportSavedUnconfirmed: "Kecederaan dilaporkan.",
        stageSaved: "Peringkat dikemas kini.",
        clearanceSaved: "Pelepasan direkodkan.",
        acknowledged: "Pelepasan disahkan.",
        team: "Pasukan",
        teamHelp: "Kecederaan direkodkan pada pasukan pemain.",
        child: "Anak",
        player: "Pemain",
        playerFallback: "Pemain",
        u18: "Bawah 18",
        noClub: "Tiada kelab",
        injuryType: "Jenis kecederaan",
        injuryDate: "Tarikh",
        notes: "Nota",
        save: "Simpan",
        loading: "Memuatkan...",
        noTeams: "Tiada pasukan dipautkan kepada peranan jurulatih atau pengurus anda.",
        noChildren: "Tiada anak yang dipautkan lagi.",
        noInjuries: "Tiada rekod kecederaan kebajikan.",
        loadError: "Rekod kebajikan tidak dapat dimuatkan.",
        markComplete: "Tandakan peringkat selesai",
        stageComplete: "Selesai",
        stagePending: "Belum bermula",
        clearPlayer: "Benarkan pemain",
        acknowledge: "Sahkan pelepasan",
        parentAckTitle: "Pengesahan ibu bapa diperlukan",
        parentAckHelp: "Pemain bawah 18 tahun kekal tidak boleh bermain sehingga ibu bapa yang dipautkan mengesahkan pelepasan ini.",
        playerUnavailableHelp: "Anda tidak boleh bermain sehingga jurulatih atau pegawai perubatan membenarkan anda. Pemain bawah 18 tahun juga memerlukan pengesahan ibu bapa.",
        playerAvailableHelp: "Anda tersedia untuk dipilih.",
        concussionTitle: "Disyaki gegaran otak",
        concussionHelp: "Keluarkan pemain serta-merta. Tiada kembali pada hari yang sama. Enam peringkat, sekurang-kurangnya 24 jam setiap satu. Turun satu peringkat jika gejala kembali. Surat doktor diperlukan sebelum sentuhan dan perlawanan.",
        availability: {
          available: "Tersedia",
          unavailable: "Tidak tersedia",
          column: "Kesediaan",
        },
        status: {
          reported: "Dilaporkan",
          in_rtp: "Kembali bermain",
          pending_parent_ack: "Menunggu ibu bapa",
          cleared: "Dibenarkan",
        },
        types: {
          suspected_concussion: "Disyaki gegaran otak",
          muscle: "Otot",
          joint: "Sendi",
          ligament: "Ligamen",
          fracture: "Patah",
          illness: "Sakit",
          other: "Lain-lain",
        },
        steps: {
          concussion_relative_rest: "Rehat relatif",
          concussion_light_aerobic: "Senaman aerobik ringan",
          concussion_sport_specific: "Latihan khusus sukan",
          concussion_non_contact: "Latihan tanpa sentuhan",
          concussion_full_contact: "Latihan sentuhan penuh",
          concussion_return: "Kembali bermain",
          general_rest: "Rehat dan perlindungan",
          general_light: "Aktiviti ringan",
          general_modified: "Latihan diubah suai",
          general_full: "Latihan penuh",
          general_return: "Kembali bermain",
        },
        stepHelp: {
          concussion_relative_rest: "Rehat relatif sekurang-kurangnya 24 jam. Tiada kembali pada hari yang sama.",
          concussion_light_aerobic: "Senaman ringan sahaja, tanpa tandukan.",
          concussion_sport_specific: "Pergerakan khusus bola sepak, tanpa tandukan atau sentuhan.",
          concussion_non_contact: "Latihan tanpa sentuhan.",
          concussion_full_contact: "Sentuhan penuh hanya selepas surat doktor dan 24 jam.",
          concussion_return: "Perlawanan hanya selepas surat doktor dan 24 jam.",
          general_rest: "Lindungi kecederaan sekurang-kurangnya 24 jam.",
          general_light: "Aktiviti ringan selepas rehat selesai.",
          general_modified: "Latihan disesuaikan dengan kecederaan.",
          general_full: "Latihan penuh bersama skuad.",
          general_return: "Boleh dipilih selepas peringkat ini dan pelepasan.",
        },
        stepBlock: {
          previous: "Selesaikan peringkat sebelumnya dahulu.",
          rest: "Tempoh rehat minimum untuk peringkat ini belum cukup.",
          gap: "Tunggu 24 jam selepas peringkat sebelumnya.",
          sameDay: "Tiada kembali pada hari yang sama selepas disyaki gegaran otak.",
          clearance: "Muat naik surat doktor sebelum latihan sentuhan atau perlawanan.",
        },
        heading: {
          label: "Latihan tandukan",
          included: "Ada tandukan",
          notIncluded: "Tiada tandukan",
          none: "Tiada tandukan. Kumpulan umur ini U11 atau lebih muda.",
          limited_5: "U12-U16: paling banyak satu sesi tandukan seminggu, kira-kira 5 tandukan.",
          standard_10: "Pemain lebih dewasa: paling banyak satu sesi tandukan seminggu, kira-kira 10 tandukan.",
          age_unknown: "Tetapkan kumpulan umur program sebelum tandukan dibenarkan.",
          blocked: "Tandukan disekat untuk kumpulan umur ini.",
        },
      },
    },
  },
};

Object.assign(
  resources.en.translation,
  { modules: { ...moduleCopy.en, batchA: batchACopy.en }, authUi: authUiEn, shell: shellUiEn, kit: kitUiEn, dash: dashUiEn, wave: waveUiEn },
  enBatch,
  enMarketing,
  enToasts,
);
Object.assign(
  resources.ms.translation,
  { modules: { ...moduleCopy.ms, batchA: batchACopy.ms }, authUi: authUiMs, shell: shellUiMs, kit: kitUiMs, dash: dashUiMs, wave: waveUiMs },
  msBatch,
  msMarketing,
  msToasts,
);

i18n.use(initReactI18next);

if (typeof window !== "undefined") {
  i18n.use(LanguageDetector);
}

i18n.init({
  resources,
  fallbackLng: "en",
  defaultNS: "translation",
  debug: false,
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
  detection: {
    order: ["localStorage", "navigator", "htmlTag"],
    caches: ["localStorage"],
  },
});

i18n.addResourceBundle("en", "batchb", batchBCopy.en, true, true);
i18n.addResourceBundle("ms", "batchb", batchBCopy.ms, true, true);

if (import.meta.env.VITE_PSEUDO_LOCALE === "true") {
  registerPseudoLocale(i18n);
}

function applyHtmlLang(language: string) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = htmlLang(language);
}

i18n.on("languageChanged", applyHtmlLang);
applyHtmlLang(i18n.language || "en");

export default i18n;
